import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Bell,
  GitBranch,
  KeyRound,
  Network,
  Terminal,
  UserRound,
  type LucideIcon
} from 'lucide-react'
import { FLAGS } from '@atbash-labs/ontology-flags'
import { isCurrentUserAdminOrGlobalAdminSelector } from '@axilon-apps/domains-identity/state'
import { useAppSelector, useListFlaggedNodesQuery } from '@axilon-apps/domains-ontology-ui-state'

import { Badge } from '../../components/ui/badge'
import { useFlag } from '../../components/flags/useFlag'
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs'
import { useDebugMode } from '../../lib/debug-mode'
import { isCoreView, useOperatorView } from '../../lib/operator-view-flag'
import { AdvancedSection } from './AdvancedSection'
import { ApiKeysSection } from './components/api-keys/ApiKeysSection'
import { BranchesTab } from './BranchesTab'
import { ConnectivitySection } from './ConnectivitySection'
import { GeneralSection } from './GeneralSection'
import { NotificationsSettings } from './notifications/NotificationsSettings'
import {
  parseSettingsNav,
  tabParamForNav,
  type AdvancedPanel,
  type ConnectivityPanel,
  type SettingsNav,
  type SettingsSection
} from './settings-nav'

const SECTIONS: ReadonlyArray<{
  value: SettingsSection
  label: string
  icon: LucideIcon
}> = [
  { value: 'general', label: 'General', icon: UserRound },
  { value: 'notifications', label: 'Notifications', icon: Bell },
  { value: 'branches', label: 'Branches', icon: GitBranch },
  { value: 'connectivity', label: 'Connectivity', icon: Network },
  { value: 'api-keys', label: 'API keys', icon: KeyRound },
  { value: 'advanced', label: 'Advanced', icon: Terminal }
]

const SettingsPage = () => {
  const debugMode = useDebugMode()
  const coreView = isCoreView(useOperatorView())
  const isAdmin = useAppSelector(isCurrentUserAdminOrGlobalAdminSelector)
  const showApiKeys = isAdmin && !coreView
  const showMobileApp = useFlag(FLAGS.UI_SETTINGS_MOBILE_APP)
  const [searchParams, setSearchParams] = useSearchParams()
  const flaggedQuery = useListFlaggedNodesQuery()
  const flaggedCount = flaggedQuery.data?.length ?? 0

  const sections = useMemo(
    () =>
      SECTIONS.filter(
        (section) =>
          (!coreView || section.value !== 'branches') &&
          (showApiKeys || section.value !== 'api-keys')
      ),
    [coreView, showApiKeys]
  )

  const nav = useMemo(
    () =>
      parseSettingsNav(searchParams.get('tab'), debugMode, coreView, showApiKeys, showMobileApp),
    [searchParams, debugMode, coreView, showApiKeys, showMobileApp]
  )

  const writeNav = useCallback(
    (next: SettingsNav) => {
      const params = new URLSearchParams(searchParams)
      const tab = tabParamForNav(next, debugMode, showApiKeys, showMobileApp)
      if (tab === null) params.delete('tab')
      else params.set('tab', tab)
      setSearchParams(params, { replace: false })
    },
    [debugMode, searchParams, setSearchParams, showApiKeys, showMobileApp]
  )

  const goSection = useCallback(
    (section: SettingsSection) => {
      writeNav({
        section,
        generalPanel: 'hub',
        connectivityPanel: 'hub',
        advancedPanel: 'hub'
      })
    },
    [writeNav]
  )

  const setConnectivityPanel = useCallback(
    (connectivityPanel: ConnectivityPanel) => {
      writeNav({ ...nav, section: 'connectivity', connectivityPanel })
    },
    [nav, writeNav]
  )

  const setAdvancedPanel = useCallback(
    (advancedPanel: AdvancedPanel) => {
      writeNav({ ...nav, section: 'advanced', advancedPanel })
    },
    [nav, writeNav]
  )

  return (
    <div className="mx-auto flex w-full max-w-[68rem] flex-col gap-3.5">
      <header>
        <h1 className="m-0 text-xl font-semibold tracking-tight text-ink">Settings</h1>
        <p className="mt-1 text-13 text-ink-3">Workspace, branches, and connections.</p>
      </header>

      <Tabs
        value={nav.section}
        onValueChange={(next) => goSection(next as SettingsSection)}
        className="gap-0"
      >
        <TabsList
          variant="line"
          className="h-auto w-full justify-start gap-1 rounded-none border-b border-border bg-transparent p-0"
        >
          {sections.map(({ value, label, icon: Icon }) => (
            <TabsTrigger
              key={value}
              value={value}
              className="h-auto gap-1.5 rounded-none px-2.5 pb-2.5 pt-1.5 text-13 font-medium after:bottom-[-1px] after:h-0.5"
            >
              <Icon className="size-3.5" strokeWidth={1.75} />
              {label}
              {value === 'advanced' && flaggedCount > 0 ? (
                <Badge variant="secondary" className="bg-st-down-bg px-1.5 text-11 text-st-down">
                  {flaggedCount}
                </Badge>
              ) : null}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div
        key={`${nav.section}:${nav.generalPanel}:${nav.connectivityPanel}:${nav.advancedPanel}`}
        className="min-w-0 duration-200 animate-in fade-in"
      >
        {nav.section === 'general' ? (
          <GeneralSection panel={nav.generalPanel} showMobileApp={showMobileApp} />
        ) : nav.section === 'notifications' ? (
          <NotificationsSettings />
        ) : nav.section === 'branches' ? (
          <BranchesTab />
        ) : nav.section === 'connectivity' ? (
          <ConnectivitySection panel={nav.connectivityPanel} onPanelChange={setConnectivityPanel} />
        ) : nav.section === 'api-keys' ? (
          <ApiKeysSection />
        ) : (
          <AdvancedSection
            panel={nav.advancedPanel}
            debugMode={debugMode}
            onPanelChange={setAdvancedPanel}
          />
        )}
      </div>
    </div>
  )
}

export default SettingsPage
